import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Header from '../../components/Header';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';

function Turnover() {
  const { t } = useTranslation();

  const [values, setValues] = useState({
    employeeReplacementCost: 2750,
    annualTurnoverRate: 0.49,
    eligibleEmployees: undefined,
    adoptionRate: 0.28,
    turnoverReduction: 0.2,
  });

  const calculations = useMemo(() => {
    const {
      employeeReplacementCost,
      annualTurnoverRate,
      eligibleEmployees,
      adoptionRate,
      turnoverReduction,
    } = values;

    const turnover = annualTurnoverRate * (1 - turnoverReduction);
    const replacementCostSaving =
      employeeReplacementCost *
      annualTurnoverRate *
      eligibleEmployees *
      adoptionRate *
      turnoverReduction;
    const subscriptionFees = eligibleEmployees * adoptionRate * 65;
    const netSaving = replacementCostSaving - subscriptionFees;
    const roi = replacementCostSaving / subscriptionFees;

    return { turnover, replacementCostSaving, subscriptionFees, netSaving, roi };
  }, [values]);

  const handleFocus = event => event.target.select();

  return (
    <Container className="bo-mobile overflow-x-hidden">
      <Header turnover />
      <div className="bo-rounded-container py-4">
        <Row className="mx-3 flex-column">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <p className="bo-text mb-0">{t('pages.turnover.employeeReplacementCost')}</p>
            <NumericFormat
              className="bo-input bo-w-100 text-end"
              value={values.employeeReplacementCost}
              customInput={Form.Control}
              onValueChange={({ floatValue }) =>
                setValues(prev => ({ ...prev, employeeReplacementCost: floatValue }))
              }
              allowNegative={false}
              prefix="$"
              thousandSeparator=","
              decimalScale={0}
              inputMode="numeric"
              onFocus={handleFocus}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 mt-3">
            <p className="bo-text mb-0">{t('pages.turnover.annualTurnoverRate')}</p>
            <NumericFormat
              className="bo-input bo-w-100 text-end"
              value={
                values.annualTurnoverRate
                  ? values.annualTurnoverRate * 100
                  : values.annualTurnoverRate
              }
              customInput={Form.Control}
              onValueChange={({ floatValue }) =>
                setValues(prev => ({ ...prev, annualTurnoverRate: floatValue / 100 }))
              }
              allowNegative={false}
              suffix="%"
              thousandSeparator=","
              decimalScale={0}
              inputMode="numeric"
              onFocus={handleFocus}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 mt-3">
            <p className="bo-text mb-0">{t('pages.turnover.eligibleEmployees')}</p>
            <NumericFormat
              className="bo-input bo-w-100 text-end"
              value={values.eligibleEmployees}
              customInput={Form.Control}
              onValueChange={({ floatValue }) =>
                setValues(prev => ({ ...prev, eligibleEmployees: floatValue }))
              }
              allowNegative={false}
              thousandSeparator=","
              decimalScale={0}
              inputMode="numeric"
              onFocus={handleFocus}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 mt-3">
            <p className="bo-text mb-0">{t('pages.turnover.adoptionRate')}</p>
            <NumericFormat
              className="bo-input bo-w-100 text-end"
              value={values.adoptionRate ? values.adoptionRate * 100 : values.adoptionRate}
              customInput={Form.Control}
              onValueChange={({ floatValue }) =>
                setValues(prev => ({ ...prev, adoptionRate: floatValue / 100 }))
              }
              allowNegative={false}
              suffix="%"
              thousandSeparator=","
              decimalScale={0}
              inputMode="numeric"
              onFocus={handleFocus}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 mt-3">
            <p className="bo-text mb-0">{t('pages.turnover.turnoverReduction')}</p>
            <NumericFormat
              className="bo-input bo-w-100 text-end"
              value={
                values.turnoverReduction ? values.turnoverReduction * 100 : values.turnoverReduction
              }
              customInput={Form.Control}
              onValueChange={({ floatValue }) =>
                setValues(prev => ({ ...prev, turnoverReduction: floatValue / 100 }))
              }
              allowNegative={false}
              suffix="%"
              thousandSeparator=","
              decimalScale={0}
              inputMode="numeric"
              onFocus={handleFocus}
            />
          </div>
          <p className="bo-text-medium mt-2 mb-0">
            {t('pages.turnover.bridgeover_users')}
            <strong>
              {calculations.turnover
                ? `${Number(calculations.turnover * 100).toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}%`
                : '--'}
            </strong>
          </p>

          <div className="d-flex align-items-center gap-2 mt-3">
            <hr className="w-100" />
            <p className="text-nowrap bo-text bo-text-bold mb-0">
              {t('pages.turnover.calculated_annual_saving')}
            </p>
            <hr className="w-100" />
          </div>
          <div className="d-flex flex-column gap-2 mt-3">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <p className="bo-text bo-text-bold mb-0">
                {t('pages.turnover.replacement_cost_saving')}
              </p>
              <p className="bo-text bo-larger mb-0">
                {!Number.isNaN(calculations.replacementCostSaving)
                  ? `$${Number(calculations.replacementCostSaving).toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    })}`
                  : '--'}
              </p>
            </div>
            <div className="d-flex align-items-center justify-content-between gap-3">
              <p className="bo-text bo-text-bold mb-0">
                {t('pages.turnover.bridgeover_subscription_fees')}
              </p>
              <p className="bo-text bo-larger mb-0">
                {!Number.isNaN(calculations.replacementCostSaving)
                  ? `$${Number(calculations.subscriptionFees).toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    })}`
                  : '--'}
              </p>
            </div>
          </div>
        </Row>
        <Row className="bo-bg-tertiary mt-3 px-4 py-2">
          <div className="d-flex flex-column gap-2 px-3">
            <div className="d-flex justify-content-between">
              <p className="bo-larger-x2 fw-bold text-white mb-0">
                {t('pages.turnover.net_saving')}
              </p>
              <p className="bo-larger-x2 fw-bold text-white mb-0">
                {!Number.isNaN(calculations.netSaving)
                  ? `${calculations.netSaving < 0 ? '-' : ''}$${Number(
                      Math.abs(calculations.netSaving),
                    ).toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    })}`
                  : '--'}
              </p>
            </div>
            <div className="d-flex justify-content-between">
              <p className="bo-text bo-text-bold text-white mb-0">{t('pages.turnover.roi')}</p>
              <p className="bo-larger fw-bold text-white mb-0">
                {!Number.isNaN(calculations.roi)
                  ? `X ${Number(calculations.roi).toLocaleString('en-US', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}`
                  : '--'}
              </p>
            </div>
          </div>
        </Row>
      </div>
    </Container>
  );
}

export default Turnover;
